import {
  useRef,
  type ClipboardEvent,
  type FC,
  type KeyboardEvent
} from "react";
import { Cancel01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { cx } from "shared/utils";
import { validateEmail } from "shared/utils/valid-email";

interface EmailPillsInputProps {
  id: string;
  emails: string[];
  draft: string;
  onDraftChange: (value: string) => void;
  onChange: (emails: string[]) => void;
  placeholder?: string;
  // Shown instead of `placeholder` once at least one address is in.
  addMorePlaceholder?: string;
  // Characters that commit the draft as an address, besides Enter. A space
  // also splits on any whitespace, so pasted lists break on line breaks too.
  separators?: string[];
  // Addresses beyond this cap stay in the draft and onLimitExceeded is called.
  maxEmails?: number;
  onLimitExceeded?: () => void;
  size?: "sm" | "md";
  // Red border, for a field-level error shown by the parent.
  hasError?: boolean;
}

const DEFAULT_SEPARATORS = [","];

const escapeForCharClass = (char: string) =>
  char === " " ? "\\s" : char.replace(/[\\\]^-]/g, "\\$&");

const separatorPattern = (separators: string[]) =>
  new RegExp(`[${separators.map(escapeForCharClass).join("")}]+`);

const dedupeAppend = (
  existing: string[],
  additions: string[],
  maxEmails: number
): { next: string[]; rejected: string[] } => {
  const seen = new Set(existing.map((email) => email.toLowerCase()));
  const next = [...existing];
  const rejected: string[] = [];
  additions.forEach((raw) => {
    const email = raw.trim();
    const key = email.toLowerCase();
    if (!email || seen.has(key)) return;
    if (next.length >= maxEmails) {
      rejected.push(email);
      return;
    }
    seen.add(key);
    next.push(email);
  });
  return { next, rejected };
};

export const EmailPillsInput: FC<EmailPillsInputProps> = ({
  id,
  emails,
  draft,
  onDraftChange,
  onChange,
  placeholder,
  addMorePlaceholder,
  separators = DEFAULT_SEPARATORS,
  maxEmails = Number.POSITIVE_INFINITY,
  onLimitExceeded,
  size = "md",
  hasError = false
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const splitter = separatorPattern(separators);

  // Addresses turned away by the cap go back into the input, so the user sees
  // what wasn't added instead of losing it. The draft is updated before the
  // limit is reported: callers clear their field error on a draft change, and
  // the cap message has to be the last word.
  const commit = (tokens: string[], rest = "") => {
    const { next, rejected } = dedupeAppend(emails, tokens, maxEmails);
    if (next.length !== emails.length) onChange(next);
    onDraftChange([...rejected, rest].filter(Boolean).join(", "));
    if (rejected.length > 0) onLimitExceeded?.();
  };

  // The draft can hold several addresses (ones the cap turned away), so it is
  // split like any typed list.
  const commitDraft = () => commit(draft.split(splitter));

  const handleChange = (value: string) => {
    if (!splitter.test(value)) {
      onDraftChange(value);
      return;
    }
    const parts = value.split(splitter);
    commit(parts.slice(0, -1), parts[parts.length - 1]);
  };

  // An email input drops a typed trailing space from its value, so separator
  // keys commit here rather than through handleChange.
  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" || separators.includes(event.key)) {
      if (event.key === "Enter" || draft.trim() !== "") event.preventDefault();
      if (draft.trim() !== "") commitDraft();
      return;
    }
    if (event.key === "Backspace" && draft === "" && emails.length > 0)
      onChange(emails.slice(0, -1));
  };

  // A pasted list becomes tags at once, the last entry included.
  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const text = event.clipboardData.getData("text");
    if (!splitter.test(text)) return;
    event.preventDefault();
    commit([draft, ...text.split(splitter)]);
  };

  const handleBlur = () => {
    if (draft.trim()) commitDraft();
  };

  const shownPlaceholder =
    emails.length > 0 ? (addMorePlaceholder ?? "") : placeholder;

  return (
    <div
      onClick={() => inputRef.current?.focus()}
      className={cx(
        "flex flex-wrap items-center gap-1.5 max-h-[120px] overflow-y-auto rounded-lg border bg-background-1000",
        size === "sm" ? "min-h-9 px-2 py-1" : "min-h-12 px-3 py-2",
        hasError
          ? "border-danger-500"
          : "border-alpha-100 focus-within:border-alpha-200"
      )}
    >
      {emails.map((email) => {
        const invalid = !validateEmail(email);
        return (
          <span
            key={email}
            className={cx(
              "flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-md text-sm",
              invalid
                ? "bg-danger-50 text-danger-700"
                : "bg-background-800 text-content-1000"
            )}
          >
            <span className="truncate max-w-[200px]">{email}</span>
            <span
              role="button"
              tabIndex={0}
              aria-label={`Remove ${email}`}
              onClick={(event) => {
                event.stopPropagation();
                onChange(emails.filter((item) => item !== email));
              }}
              onKeyDown={(event) => {
                if (event.key !== "Enter" && event.key !== " ") return;
                event.preventDefault();
                event.stopPropagation();
                onChange(emails.filter((item) => item !== email));
              }}
              className="grid place-items-center size-4 rounded hover:bg-alpha-100 text-content-500"
            >
              <HugeiconsIcon icon={Cancel01Icon} size={12} />
            </span>
          </span>
        );
      })}
      <input
        ref={inputRef}
        id={id}
        type="email"
        value={draft}
        placeholder={shownPlaceholder}
        onChange={(event) => handleChange(event.target.value)}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onBlur={handleBlur}
        className={cx(
          "flex-1 min-w-[140px] bg-transparent border-0 shadow-none appearance-none outline-none focus:outline-none focus:ring-0 focus:border-0 focus:shadow-none text-content-1000 placeholder:text-content-300",
          size === "sm" ? "text-sm py-0.5" : "text-base"
        )}
      />
    </div>
  );
};
